import React from 'react'
import { CollabFieldWrapper } from '../CollabFieldWrapper'
import { useGetQueryData } from '@/utils/react-query-hooks/general'

/**
 * The lock-derivation dance every CollabFieldWrapper call site repeats:
 * look up the lock for a valuePath on a collab session and decide whether a
 * PEER holds it (a lock with our own socketId is ours, not a block).
 *
 * `session` is anything exposing { locks, socketId, acquireLock, releaseLock }
 * — the content session's CollabEditContext value and reports' reportCollab
 * both do. Pass null/undefined when no session is active.
 */
const useCollabFieldLock = (session, valuePath) => {
	const lock = session?.locks?.[valuePath]
	return {
		lock,
		lockedByOther: !!(lock && lock.socketId !== session?.socketId),
	}
}

/**
 * Conditional CollabFieldWrapper: with an active session, children get the
 * full lock bracket (focus→acquire, blur→release, locked-by-other overlay);
 * without one there is no lock to hold, so children render bare.
 *
 * `compactChip`: for one-line hosts (a title crumb) the chip flows inline
 * after the content instead of the absolute badge landing on the text.
 */
export const CollabLockedField = ({ session, valuePath, compactChip, style, children }) => {
	const { lock, lockedByOther } = useCollabFieldLock(session, valuePath)
	// Same user in another tab: the chip says so instead of showing their own name.
	const { user } = useGetQueryData('userData') || {}
	const lockHolderIsSelf = !!(lock?.userId && user?._id && lock.userId === user._id)
	if (!session) return children
	return (
		<CollabFieldWrapper
			valuePath={valuePath}
			lockedByOther={lockedByOther}
			lockHolderName={lock?.userName}
			lockHolderIsSelf={lockHolderIsSelf}
			compactChip={compactChip}
			// pass-through, not curried: the wrapper calls it with its own
			// presence-check callback as a second argument
			acquireLock={session.acquireLock}
			releaseLock={session.releaseLock}
			style={style}
		>
			{children}
		</CollabFieldWrapper>
	)
}
